import type { FairshareConfig } from "./types";
import { debug } from "../utils/debug";
import { getBrowserInfo } from "../utils/environment";
import {
  DEFAULT_PATH_ATTRIBUTION_PATTERN,
  DEFAULT_SUBDOMAIN_ATTRIBUTION_PATTERN,
} from "./types";

/**
 * Attribution source types
 */
export enum AttributionSourceType {
  SHARE_GUID = "share_guid",
}

/**
 * Attribution data
 */
export interface AttributionData {
  /** Share GUID attribution */
  shareGuid?: {
    identifier: string;
    timestamp: number;
  };
}

/**
 * Interface defining attribution data for API requests
 */
export interface AttributionLookupData {
  /** Share GUID for attribution */
  shareGuid?: string;
}

/**
 * Manages attribution data for the FairShare SDK
 */
export class AttributionManager {
  private attributionData: AttributionData = {};
  private config: FairshareConfig;

  /**
   * Create a new AttributionManager
   * @param config FairShare configuration
   * @param storageManager Storage manager instance
   */
  constructor(config: FairshareConfig) {
    // First create a clone of the config to avoid modifying the original
    const attributionConfig = { ...config };

    // Initialize attributionSettings properly as per the type definition
    // AttributionSettings requires either pathAttributionPattern or subdomainAttributionPattern
    attributionConfig.attributionSettings =
      attributionConfig.attributionSettings ?? {
        pathAttributionPattern: "", // Default empty value that we'll update/remove as needed
      };

    // Apply path attribution logic - only apply if:
    // 1. User explicitly enabled it via enablePathAttribution flag, or
    // 2. User provided a custom pathAttributionPattern (implies intent to use it)
    const hasCustomPathPattern =
      !!attributionConfig.attributionSettings.pathAttributionPattern;
    const isPathAttributionEnabled =
      attributionConfig.enablePathAttribution === true;

    // Apply path attribution pattern only if enabled or if custom pattern provided
    if (isPathAttributionEnabled || hasCustomPathPattern) {
      // If enabled but no custom pattern, use the default
      if (!hasCustomPathPattern) {
        attributionConfig.attributionSettings.pathAttributionPattern =
          DEFAULT_PATH_ATTRIBUTION_PATTERN;
      }
    } else {
      // Ensure we don't have a path attribution pattern if not enabled
      if (attributionConfig.attributionSettings.pathAttributionPattern) {
        attributionConfig.attributionSettings.pathAttributionPattern =
          undefined;
      }
    }

    // Only apply subdomain attribution in these cases:
    // 1. User explicitly enabled it via enableSubdomainAttribution flag
    // 2. User provided a custom subdomainAttributionPattern (implies intent to use it)
    const hasCustomSubdomainPattern =
      !!attributionConfig.attributionSettings.subdomainAttributionPattern;
    const isSubdomainAttributionEnabled =
      attributionConfig.enableSubdomainAttribution === true;

    // Apply subdomain attribution pattern only if enabled or if custom pattern provided
    if (isSubdomainAttributionEnabled || hasCustomSubdomainPattern) {
      // If enabled but no custom pattern, use the default
      if (!hasCustomSubdomainPattern) {
        attributionConfig.attributionSettings.subdomainAttributionPattern =
          DEFAULT_SUBDOMAIN_ATTRIBUTION_PATTERN;
      }
    } else {
      // Ensure we don't have a subdomain attribution pattern if not enabled
      if (attributionConfig.attributionSettings.subdomainAttributionPattern) {
        attributionConfig.attributionSettings.subdomainAttributionPattern =
          undefined;
      }
    }

    // Final check to ensure attributionSettings is valid according to the type definition
    // It must have at least one attribution pattern defined
    if (
      !attributionConfig.attributionSettings.pathAttributionPattern &&
      !attributionConfig.attributionSettings.subdomainAttributionPattern
    ) {
      // If both patterns are undefined, set a default path pattern to satisfy the type
      attributionConfig.attributionSettings.pathAttributionPattern = "";
    }

    this.config = attributionConfig;
  }

  /**
   * Capture attribution from URL (path-based)
   * @returns The attribution data or null if not found
   */
  public getAttributionData(): AttributionData | null {
    if (typeof window === "undefined") {
      return null;
    }

    debug("Starting attribution capture...");

    // Reset attribution data each time to ensure we always use fresh URL data
    this.attributionData = {};

    try {
      // Get browser info
      const { url } = getBrowserInfo();
      debug(`Capturing attribution from URL: ${url}`);
      const parsedUrl = new URL(url);

      // Attempt to capture share GUID from the current URL
      const attributionCaptured = this.captureShareGuid(parsedUrl);

      debug(
        attributionCaptured
          ? "Captured new attribution from URL"
          : "No attribution found in URL",
      );

      // Return the attribution data
      return Object.keys(this.attributionData).length > 0
        ? this.attributionData
        : null;
    } catch (error) {
      debug("Error capturing attribution", error);
    }

    return null;
  }

  /**
   * Capture share GUID from URL path
   * @param parsedUrl The parsed URL
   * @returns True if share GUID was captured, false otherwise
   */
  private captureShareGuid(parsedUrl: URL): boolean {
    // Extract share GUID (previously pathAttribution)
    let shareGuid = this.extractShareGuid(parsedUrl);

    // If no path-based attribution found, try subdomain attribution
    shareGuid ??= this.extractSubdomainShareGuid(parsedUrl);

    if (shareGuid) {
      debug("Processing share GUID from URL");

      // Set share GUID for this attribution event
      this.attributionData.shareGuid = {
        identifier: shareGuid,
        timestamp: Date.now(),
      };
      debug(`Share GUID captured: ${shareGuid}`);
      return true;
    }

    return false;
  }

  /**
   * Extract share GUID from URL path
   * @param url Parsed URL object
   * @returns Share GUID or null if not found
   *
   * Example usage for influencer paths:
   * - Set pathAttributionPattern: "/influencer/([^/]+)" to capture "mike" from "/influencer/mike"
   * - For more complex paths like "/product/123/by/jane", use "/by/([^/]+)" to capture "jane"
   * - Supports both regular paths and hash-based routing (e.g., /#/influencer/mike)
   */
  private extractShareGuid(url: URL): string | null {
    try {
      // Extract using regex pattern
      const pattern =
        this.config.attributionSettings?.pathAttributionPattern ??
        DEFAULT_PATH_ATTRIBUTION_PATTERN;

      // Make sure pattern is a valid string
      if (typeof pattern === "string") {
        const regex = new RegExp(pattern);

        // First try matching against the pathname (standard routing)
        const pathMatch = url.pathname.match(regex);
        if (pathMatch?.[1]) {
          debug(`Found share GUID match in pathname: ${pathMatch[1]}`);
          return pathMatch[1];
        }

        // If no match in pathname, try matching against the hash (hash-based routing)
        // Remove the initial '#' if present
        const hashPath = url.hash.startsWith("#")
          ? url.hash.substring(1)
          : url.hash;
        if (hashPath) {
          const hashMatch = hashPath.match(regex);
          if (hashMatch?.[1]) {
            debug(`Found share GUID match in hash fragment: ${hashMatch[1]}`);
            return hashMatch[1];
          }
        }

        debug(
          `No match found for pattern: ${pattern} in either pathname or hash`,
        );
      }
    } catch (error) {
      debug("Error extracting share GUID", error);
    }

    return null;
  }

  /**
   * Extract share GUID from URL subdomain
   * @param url Parsed URL object
   * @returns Share GUID or null if not found
   *
   * Example usage for subdomain attribution:
   * - Set subdomainAttributionPattern: "^([^.]+)" to capture "foo" from "foo.test.com"
   * - Works with multiple subdomain levels, e.g., "foo.staging.test.com"
   */
  private extractSubdomainShareGuid(url: URL): string | null {
    try {
      // Skip subdomain extraction entirely if subdomain attribution isn't explicitly enabled
      // This ensures subdomain attribution is strictly opt-in
      if (!this.config.enableSubdomainAttribution) {
        debug("Subdomain attribution is disabled, skipping extraction");
        return null;
      }

      // Extract using regex pattern - only if explicitly provided in config
      const pattern =
        this.config.attributionSettings?.subdomainAttributionPattern;

      // If no pattern is specified (even with enableSubdomainAttribution=true), skip extraction
      if (!pattern) {
        debug("No subdomain attribution pattern provided, skipping extraction");
        return null;
      }

      // Make sure pattern is a valid string
      if (typeof pattern === "string") {
        const regex = new RegExp(pattern);

        // Extract hostname and apply the pattern
        const { hostname } = url;
        const subdomainMatch = hostname.match(regex);

        if (subdomainMatch?.[1]) {
          debug(`Found share GUID match in subdomain: ${subdomainMatch[1]}`);
          return subdomainMatch[1];
        }

        debug(
          `No match found for subdomain pattern: ${pattern} in hostname: ${hostname}`,
        );
      }
    } catch (error) {
      debug("Error extracting subdomain share GUID", error);
    }

    return null;
  }

  /**
   * Convert attribution data to attribution lookup format for API requests
   *
   * @returns Attribution lookup object with typed properties
   */
  public convertToAttributionLookup(): AttributionLookupData | undefined {
    // Ensure we have fresh attribution data
    this.getAttributionData();

    const result: AttributionLookupData = {};

    // Add share GUID if available
    if (this.attributionData.shareGuid) {
      result.shareGuid = this.attributionData.shareGuid.identifier;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  /**
   * Get the current share GUID
   * @returns The current share GUID or null if not found
   */
  public getCurrentShareGuid(): string | null {
    // Always ensure we have fresh attribution data
    this.getAttributionData();
    return this.attributionData.shareGuid?.identifier ?? null;
  }
}
